import { InputEditor } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/type.ts';
import { undefined } from 'zod';
import { Input_Base } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Base.ts';
import { CustomEventWithDetail, html, Html, View } from 'rune-ts';
import {
  ChangeOption,
  InputOption,
  SpoOption,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_AddOption/InputOption.ts';
import klass from './Input_AddOption.module.scss';
import { StudioButton } from '@services/MarppleShop/shared/components/atoms/StudioButton/StudioButton.ts';
import { Alert } from '@services/MarppleShop/shared/components/cells/Alert/Alert.ts';
import { noop } from '@fxts/core';

export class ApplyNewSpoItems extends CustomEventWithDetail<{
  spo_options: SpoOption[];
}> {}

interface InputAddOption extends InputEditor {
  value: SpoOption[];
}

const APPLY_BUTTON_LABEL = '적용하기';

export class Input_AddOption extends Input_Base<InputAddOption> {
  private MAX_COUNT = 3;
  private spo_options = this.data.value.slice(0, this.MAX_COUNT);

  private applyOptionsButton = new StudioButton({
    custom_klass: `${klass.button} ${klass.wide}`,
    label: APPLY_BUTTON_LABEL,
    color: 'sky',
    size: 'free',
    is_disabled: !this.spo_options.length,
  });

  private optionViewMaps = this.getOptionViewMaps();

  protected override inputTemplate(): Html | View {
    return html`
      <div class="${klass.root}">
        <div class="${klass.option_container}">
          ${this.optionViewMaps.map((view_map) => {
            return html`
              <div class="${klass.option_wrap} ${view_map.buttonAddView ? klass.has_add_button : ''}">
                ${view_map.inputOptionView} ${view_map.buttonDeleteView} ${view_map.buttonAddView ?? ''}
              </div>
            `;
          })}
        </div>
        ${this.applyOptionsButton}
      </div>
    `;
  }

  override onRender() {
    /* 옵션명 및 옵션값 이벤트 */
    this.delegate(ChangeOption, InputOption, (_, optionView) => {
      /* 삭제 및 추가버튼 활/비활성 */
      this.optionViewMaps.some(({ inputOptionView, buttonDeleteView, buttonAddView }, index) => {
        if (inputOptionView === optionView) {
          // 항목이 1개뿐인 첫번째 옵션이면서 비어있는 경우에만 삭제 버튼 disable
          buttonDeleteView.setDisable(
            index === 0 && this.optionViewMaps.length === 1 && optionView.isAllEmpty(),
          );
          buttonAddView?.setDisable(!optionView.isCompleted());
          return true;
        }

        return false;
      });

      this.setApplyOptionButtonDisable();
    });

    this.delegate('click', StudioButton, (_, buttonView) => {
      /* 적용하기 버튼 */
      if (buttonView.getLabel() === APPLY_BUTTON_LABEL) {
        this.applyOptions();
        return void 0;
      }

      this.optionViewMaps.some((view_map, index) => {
        /* 삭제 버튼 */
        if (view_map.buttonDeleteView === buttonView) {
          this.removeOptionInput(view_map.inputOptionView, view_map.buttonDeleteView, index);
          return true; // 찾았으니 중단
        }

        /* 추가 버튼 */
        if (view_map.buttonAddView === buttonView) {
          this.addOptionInput(index);
          return true; // 찾았으니 중단
        }

        return false; // 계속 찾기
      });
    });
  }

  private getDeleteButton(is_disabled: boolean) {
    return new StudioButton({
      custom_klass: klass.button,
      label: '삭제',
      color: 'line_gray',
      size: 'free',
      is_disabled,
    });
  }

  private getAddButton(is_disabled: boolean) {
    return new StudioButton({
      custom_klass: klass.button,
      label: '추가',
      color: 'line_gray',
      size: 'free',
      is_disabled,
    });
  }

  private removeOptionInput(optionView: InputOption, buttonDeleteView: StudioButton, index: number) {
    /* 항목이 1개뿐이고 첫번째 옵션에서 삭제버튼을 누르는 경우에만 초기화 */
    if (index === 0 && this.optionViewMaps.length === 1) {
      optionView.removeOptionInfo();
      buttonDeleteView.setDisable(true);
    } else {
      // 해당 인덱스의 옵션 뷰맵 제거
      this.optionViewMaps.splice(index, 1);

      // 마지막 옵션에 추가 버튼이 있어야 하는지 확인하고 추가
      const lastIndex = this.optionViewMaps.length - 1;
      if (lastIndex >= 0 && this.optionViewMaps.length < this.MAX_COUNT) {
        const lastOptionViewMap = this.optionViewMaps[lastIndex];
        // 기존 마지막 옵션에 추가 버튼이 없다면 추가
        if (lastOptionViewMap && !lastOptionViewMap.buttonAddView) {
          lastOptionViewMap.buttonAddView = this.getAddButton(
            !lastOptionViewMap.inputOptionView.isCompleted(),
          );
        }
      }

      this.redraw();
    }
    this.setApplyOptionButtonDisable();
  }

  private addOptionInput(currentIndex: number) {
    if (this.optionViewMaps.length >= this.MAX_COUNT) {
      return;
    }

    // 현재 옵션에서 추가 버튼 제거
    if (this.optionViewMaps[currentIndex]) {
      delete this.optionViewMaps[currentIndex].buttonAddView;
    }

    // 새로운 옵션 뷰맵 생성
    const newOptionViewMap = {
      inputOptionView: new InputOption({ name: '', _: { spo_option_values: [] } }),
      buttonDeleteView: this.getDeleteButton(false),
      // 추가 후에도 최대 개수 미만이면 추가 버튼 생성
      ...(this.optionViewMaps.length + 1 < this.MAX_COUNT ? { buttonAddView: this.getAddButton(true) } : {}),
    };

    // 새 옵션 추가
    this.optionViewMaps.push(newOptionViewMap);

    // 재렌더링 및 적용 버튼 상태 업데이트
    this.redraw();
    this.setApplyOptionButtonDisable();
  }

  private applyOptions() {
    this.spo_options = this.optionViewMaps.map(({ inputOptionView }) => inputOptionView.getOptionInfo());
    this.dispatchEvent(ApplyNewSpoItems, {
      bubbles: true,
      detail: {
        spo_options: this.spo_options,
      },
    });
  }

  private setApplyOptionButtonDisable() {
    this.applyOptionsButton.setDisable(
      !this.optionViewMaps.every(({ inputOptionView }) => inputOptionView.isCompleted()),
    );
  }

  private getOptionViewMaps() {
    return this.spo_options.length
      ? this.spo_options.map((option, index) => {
          return {
            inputOptionView: new InputOption(option),
            // 항목이 1개뿐인 첫 번째 옵션에서만 삭제 버튼 비활성화
            buttonDeleteView: this.getDeleteButton(index === 0 && this.spo_options.length === 1),
            // 가장 나중의 옵션이면서 최대 옵션 개수를 채우지 않은 상태에서만 추가 버튼을 그려준다.
            ...(this.isShowAddButton(index)
              ? {
                  buttonAddView: this.getAddButton(false),
                }
              : {}),
          };
        })
      : [
          {
            inputOptionView: new InputOption({ name: '', _: { spo_option_values: [] } }),
            buttonDeleteView: this.getDeleteButton(true),
            buttonAddView: this.getAddButton(true),
          },
        ];
  }

  private isShowAddButton(index: number) {
    return index === this.spo_options.length - 1 && this.spo_options.length < this.MAX_COUNT;
  }

  getName() {
    return this.data.name;
  }

  getValue() {
    return undefined;
  }

  setError(is_error: boolean, message: string) {
    is_error &&
      Alert.open({
        title: message,
      }).then(noop);
  }
}
